import {
  Button,
  CheckInput,
  Modal,
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
  addToast,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { getCurrencies } from '../../../../../sections/Currencies/api';
import { CostCenterItem, getCostCenters } from '../../CostCenters/api';
import {
  ChargeItem,
  ChargeItemCodeTakenError,
  ChargeItemInvoiceType,
  ChargeItemPayload,
  createChargeItem,
  updateChargeItem,
} from '../api';

const dictPrefix = 'financialPortal.settings.chargeItems';

export const NAME_MAX_LENGTH = 100;
export const CODE_MAX_LENGTH = 50;
export const DESCRIPTION_MAX_LENGTH = 255;

/**
 * Un ítem puede no tener centro de costo, y esa es la opción por defecto. El
 * `Select` no admite `null` como valor de opción, así que el "sin centro"
 * viaja con este centinela y se traduce a `null` al guardar.
 */
export const NO_COST_CENTER = 'none';

const INVOICE_TYPES: ChargeItemInvoiceType[] = ['afecta', 'exenta'];

interface FormValues {
  name: string;
  code: string;
  costCenter: SelectOptionType | null;
  description: string;
  currency: SelectOptionType | null;
  amount: string;
  invoiceType: SelectOptionType | null;
  appliesCollectionFee: boolean;
  appliesLateInterest: boolean;
}

interface Props {
  isOpen: boolean;
  item: ChargeItem | null;
  onClose: () => void;
  onSaved: () => void;
}

interface CurrencyOption {
  id: number;
  code: string;
}

export function ChargeItemModal({ isOpen, item, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState,
    reset,
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      code: '',
      costCenter: null,
      description: '',
      currency: null,
      amount: '',
      invoiceType: null,
      appliesCollectionFee: true,
      appliesLateInterest: true,
    },
  });

  const [costCenters, setCostCenters] = useState<CostCenterItem[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    getCostCenters().then(({ data }) => {
      if (data) {
        setCostCenters(data);
      }
    });
    getCurrencies().then(({ data }) => {
      if (data) {
        setCurrencies(
          [data.principal, ...data.secondary].map((currency) => ({
            id: currency.id,
            code: currency.code,
          })),
        );
      }
    });
  }, [isOpen]);

  const costCenterOptions = useMemo<SelectOptionType[]>(
    () => [
      { value: NO_COST_CENTER, label: t(`${dictPrefix}.form.noCostCenter`) },
      ...costCenters.map((costCenter) => ({
        value: String(costCenter.id),
        label: costCenter.name,
      })),
    ],
    [costCenters, t],
  );

  const currencyOptions = useMemo<SelectOptionType[]>(
    () =>
      currencies.map((currency) => ({
        value: String(currency.id),
        label: currency.code,
      })),
    [currencies],
  );

  const invoiceTypeOptions = useMemo<SelectOptionType[]>(
    () =>
      INVOICE_TYPES.map((type) => ({
        value: type,
        label: t(`${dictPrefix}.invoiceTypes.${type}`),
      })),
    [t],
  );

  /**
   * Precarga del formulario. Depende de los DATOS (`costCenters`,
   * `currencies`), nunca de los arrays de opciones: esos se recalculan con
   * cada `t` y, como el efecto llama a `reset`, cerrarían un ciclo
   * render → reset → render.
   */
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const noCostCenter = {
      value: NO_COST_CENTER,
      label: t(`${dictPrefix}.form.noCostCenter`),
    };
    const toCostCenterOption = (id: number | null) => {
      const found = costCenters.find((costCenter) => costCenter.id === id);
      return found
        ? { value: String(found.id), label: found.name }
        : noCostCenter;
    };
    const toCurrencyOption = (id: number | null) => {
      const found = currencies.find((currency) => currency.id === id);
      return found ? { value: String(found.id), label: found.code } : null;
    };

    if (item) {
      reset({
        name: item.name,
        code: item.code,
        costCenter: toCostCenterOption(item.costCenterId),
        description: item.description ?? '',
        currency: toCurrencyOption(item.currencyId),
        amount: item.amount === null ? '' : String(item.amount),
        invoiceType: {
          value: item.invoiceType,
          label: t(`${dictPrefix}.invoiceTypes.${item.invoiceType}`),
        },
        appliesCollectionFee: item.appliesCollectionFee,
        appliesLateInterest: item.appliesLateInterest,
      });
    } else {
      reset({
        name: '',
        code: '',
        costCenter: noCostCenter,
        description: '',
        currency: null,
        amount: '',
        invoiceType: null,
        // Ambos marcados por defecto: un cargo penaliza salvo que se diga lo
        // contrario.
        appliesCollectionFee: true,
        appliesLateInterest: true,
      });
    }
    // `t` queda fuera a propósito (ver el comentario de arriba).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, item, costCenters, currencies, reset]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (!values.currency || !values.invoiceType) {
        return;
      }
      const costCenterValue = values.costCenter?.value;
      const payload: ChargeItemPayload = {
        name: values.name.trim(),
        code: values.code.trim(),
        costCenterId:
          costCenterValue === undefined || costCenterValue === NO_COST_CENTER
            ? null
            : Number(costCenterValue),
        description: values.description.trim() || null,
        currencyId: Number(values.currency.value),
        amount: Number(String(values.amount).trim()),
        invoiceType: values.invoiceType.value as ChargeItemInvoiceType,
        appliesCollectionFee: values.appliesCollectionFee,
        appliesLateInterest: values.appliesLateInterest,
      };

      const { error } = item
        ? await updateChargeItem(item.id, payload)
        : await createChargeItem(payload);

      if (error) {
        // El código duplicado se muestra sobre el propio campo, igual que en
        // centros de costo: es un error que el usuario corrige ahí mismo.
        const body = error.data as ChargeItemCodeTakenError | undefined;
        if (body?.code === 'CODE_TAKEN') {
          setError('code', { message: t(`${dictPrefix}.form.codeTaken`) });
          return;
        }
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.saveError`),
        });
        return;
      }

      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${dictPrefix}.saveSuccess`),
      });
      onSaved();
    },
    [item, onSaved, setError, t],
  );

  const required = t(`${dictPrefix}.form.required`) as string;

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <h1 className="text-dark fs-22 mb-4 text-center">
        {t(`${dictPrefix}.form.title`)}
      </h1>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col xs={12} md={6}>
            <TextInput
              name="name"
              label={t(`${dictPrefix}.form.name`)}
              control={control}
              maxLength={NAME_MAX_LENGTH}
              rules={{ required }}
            />
          </Col>
          <Col xs={12} md={6}>
            <TextInput
              name="code"
              label={t(`${dictPrefix}.form.code`)}
              control={control}
              maxLength={CODE_MAX_LENGTH}
              rules={{ required }}
            />
          </Col>
        </Row>
        <Select
          name="costCenter"
          label={t(`${dictPrefix}.form.costCenter`)}
          control={control}
          options={costCenterOptions}
          isClearable={false}
        />
        <TextAreaInput
          name="description"
          label={t(`${dictPrefix}.form.description`)}
          placeholder={t(`${dictPrefix}.form.descriptionPlaceholder`)}
          control={control}
          maxLength={DESCRIPTION_MAX_LENGTH}
        />
        <Row>
          <Col xs={12} md={6}>
            <Select
              name="currency"
              label={t(`${dictPrefix}.form.currency`)}
              placeholder={t(`${dictPrefix}.form.selectPlaceholder`)}
              control={control}
              options={currencyOptions}
              rules={{ required }}
              isClearable={false}
            />
          </Col>
          <Col xs={12} md={6}>
            <TextInput
              name="amount"
              label={t(`${dictPrefix}.form.amount`)}
              control={control}
              rules={{
                required,
                validate: (raw: any) => {
                  const value = Number(String(raw ?? '').trim());
                  if (Number.isNaN(value) || value <= 0) {
                    return t(`${dictPrefix}.form.amountInvalid`) as string;
                  }
                  return true;
                },
              }}
            />
          </Col>
        </Row>
        <Select
          name="invoiceType"
          label={t(`${dictPrefix}.form.invoiceType`)}
          placeholder={t(`${dictPrefix}.form.selectPlaceholder`)}
          control={control}
          options={invoiceTypeOptions}
          rules={{ required }}
          isClearable={false}
        />
        <CheckInput
          name="appliesCollectionFee"
          label={t(`${dictPrefix}.form.appliesCollectionFee`)}
          control={control}
        />
        <CheckInput
          name="appliesLateInterest"
          label={t(`${dictPrefix}.form.appliesLateInterest`)}
          control={control}
        />
        <Row className="mt-4">
          <Col xs={12} md={6}>
            <Button
              outlined
              fullwidth
              text={t(`${dictPrefix}.form.cancel`)}
              onClick={onClose}
            />
          </Col>
          <Col xs={12} md={6}>
            <Button
              type="submit"
              fullwidth
              loading={formState.isSubmitting}
              text={t(`${dictPrefix}.form.save`)}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
